import { Component, ChangeDetectionStrategy, OnDestroy, inject, viewChild, ElementRef } from '@angular/core';
import {
  BehaviorSubject, concatMap, distinctUntilChanged, map, Observable, of, Subject, take, withLatestFrom, takeUntil, combineLatest, finalize,
} from 'rxjs';
import { AttributeListExportService, SupportedExtractFormats } from '../services/attribute-list-export.service';
import { Store } from '@ngrx/store';
import {
  selectColumnsForSelectedTab, selectSelectedTab, selectSortForSelectedTab,
} from '../state/attribute-list.selectors';
import { selectCQLFilters } from '../../../state/filter-state/filter.selectors';
import { selectLayers } from '../../../map/state/map.selectors';
import { HiddenLayerFunctionality } from '@tailormap-viewer/api';
import { FileHelper } from '@tailormap-viewer/shared';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-attribute-list-export-button',
    templateUrl: './attribute-list-export-button.component.html',
    styleUrls: ['./attribute-list-export-button.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButton,
        MatProgressSpinner,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatMenuItem,
        AsyncPipe,
    ],
})
export class AttributeListExportButtonComponent implements OnDestroy {
  private store$ = inject(Store);
  private exportService = inject(AttributeListExportService);

  private destroyed = new Subject();
  public supportedFormats = SupportedExtractFormats;
  private supportedFormatsSubject = new BehaviorSubject<SupportedExtractFormats[]>([]);
  private supportedFormats$: Observable<SupportedExtractFormats[]> = this.supportedFormatsSubject.asObservable();
  public showExportButton$ = this.supportedFormats$.pipe(map(formats => formats.length > 0));

  private isExportingSubject = new BehaviorSubject(false);
  public isExporting$ = this.isExportingSubject.asObservable();

  public extractProgress$ = this.exportService.extractProgress$;
  public useProgressSpinner$ = this.extractProgress$.pipe(map(progress => progress > 0));
  public exportButton = viewChild('exportButton', { read: ElementRef });

  constructor() {
    combineLatest([
      this.store$.select(selectLayers),
      this.store$.select(selectSelectedTab),
    ])
      .pipe(
        takeUntil(this.destroyed),
        distinctUntilChanged(),
        concatMap(([ layers, selectedTab ]) => {
          if (!selectedTab || selectedTab.layerId === null || typeof selectedTab.layerId === 'undefined') {
            return of([]);
          }
          const layer = layers.find(l => l.id === selectedTab.layerId);
          if (layer?.hiddenFunctionality?.includes(HiddenLayerFunctionality.export)) {
            return of([]);
          }
          return this.exportService.getExportFormats$(selectedTab.tabSourceId, selectedTab.layerId);
        }),
      )
      .subscribe(formats => this.supportedFormatsSubject.next(formats));
  }

  public ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public onExportClick(format: SupportedExtractFormats) {
    this.isExportingSubject.next(true);
    this.store$.select(selectSelectedTab)
      .pipe(
        withLatestFrom(
          this.store$.select(selectCQLFilters),
          this.store$.select(selectSortForSelectedTab),
          this.store$.select(selectColumnsForSelectedTab),
        ),
        take(1),
        concatMap(([ tab, filters, sort, columns ]) => {
          if (tab === null || typeof tab.layerId === 'undefined') {
            return of(null);
          }
          const filter = filters.get(tab.layerId);
          const attributes = columns.filter(c => c.visible).map(c => c.id);
          return this.exportService.export$({ tabSourceId: tab.tabSourceId, layerId: tab.layerId, serviceLayerName: tab.label, format, filter, sort, attributes });
        }),
        finalize(() => {
          this.isExportingSubject.next(false);
          setTimeout(() => {
            if (this.exportButton()) {
              this.exportButton()?.nativeElement.focus();
            }
          });
        }),
      )
      .subscribe(response => {
        if (response === null || AttributeListExportService.isDownloadLayerExtractResponse(response)) {
          this.isExportingSubject.next(false);
        }
        if (AttributeListExportService.isDownloadLayerExtractResponse(response)) {
          FileHelper.saveAsFile(response.file, response.fileName);
        }
      });
  }

  public isFormatSupported$(format: SupportedExtractFormats) {
    return this.supportedFormats$.pipe(map(formats => formats.includes(format)));
  }
}
